import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import { Button, Icon, Text } from '@chakra-ui/react'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { RiDeleteBinLine } from 'react-icons/ri'
import useFetch, { CachePolicies } from 'use-http'
import useToast from '../../../hooks/useToast'
import type { DeleteFileVersionErrorResponse } from '../../../interfaces'
import { MLContext } from '../../../providers'
import { MediaLibraryActions } from '../../../providers/MLProvider'

interface Props {
  isOpen: boolean
  onClose(): void
}

const DeleteFileVersionModal = ({
  isOpen,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()

  const [{ selectedFiles }, dispatch] = useContext(MLContext)

  const { response, loading, request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const deleteFileVersion = async (): Promise<void> => {
    const res = (await request.delete(
      `/api/v2/files/${selectedFiles[0].fileId}/versions/${selectedFiles[0].versionInfo.id}`,
    )) as DeleteFileVersionErrorResponse | null

    if (request.error) {
      if ([400, 404].includes(response.status)) {
        toast({
          description: res?.message,
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else {
        toast({
          description: t(
            'error_in_deleting_file_version',
            'Error in deleting file version, please try again after sometime',
          ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      }
    } else {
      dispatch({
        type: MediaLibraryActions.REMOVE_FILE_VERSIONS,
        payload: [selectedFiles[0].versionInfo.id],
      })
      toast({
        description: t(
          'file_version_deleted_successfully',
          'File version deleted successfully',
        ),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Icon
            as={RiDeleteBinLine}
            color="appBattleshipGrey"
            backgroundColor="appPaleGrey"
            boxSize={6}
            padding="0.1em"
            borderRadius="full"
            marginRight="0.5em"
          />
          {t('delete_file_version', 'Delete file version')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text color="appBattleshipGrey">
            {t(
              'delete_file_version_description',
              `This will permanently delete the selected file version. Once submitted, 
                this action can not be reversed.`,
            )}
          </Text>
        </ModalBody>
        <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
          <Button onClick={onClose} variant="outline">
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            colorScheme="appRed"
            isDisabled={loading}
            isLoading={loading}
            onClick={async () => {
              await deleteFileVersion()
              onClose()
              dispatch({
                type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
              })
            }}
          >
            {t('submit', 'Submit')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default DeleteFileVersionModal
